/**
 * ========================
 * GLOBAL MENU PROVIDER
 * ========================
 *
 * Provides app-wide navigation structure.
 *
 * Sections:
 * - App: Domain shortcuts derived from DOMAINS config (domains.tsx)
 * - User: Profile and Settings derived from GLOBAL_MENU_SECTIONS
 * - Admin: System management derived from GLOBAL_MENU_SECTIONS
 *
 * RULES:
 * - Never import UI components except icons
 * - Use createVisibilityCheck for permissions
 * - Derive menu from domains.tsx for single source of truth
 * - Role/permission requirements are synced from DOMAINS config
 */

import { IconComponents } from '@tabler/icons-react';
import { GlobalMenuProvider, MenuSection, MenuItem } from '../../layouts/menu/types';
import { createVisibilityCheck } from './BaseMenuProvider';
import { DOMAINS, GLOBAL_MENU_SECTIONS, DomainKey } from '../../config/domains';
import { cloneElement, isValidElement, ReactElement } from 'react';
import {
  getDomainPrimaryBadgeCount,
  getDomainPrimaryBadgeColor,
} from '../badges/useDomainPrimaryBadge';
import { useUserBadgesStore } from '../badges/userBadgesStore';

/**
 * Helper to resize icons from domains.tsx to 16px for menu
 */
function resizeIcon(icon: React.ReactNode): React.ReactNode {
  if (isValidElement(icon)) {
    return cloneElement(icon as ReactElement<{ size?: number }>, { size: 16 });
  }
  return icon;
}

/**
 * Generate App section menu items from DOMAINS configuration.
 * This ensures role/permission requirements are synced from domains.tsx.
 */
function generateAppMenuItems(): MenuItem[] {
  // Define the order of domains in the App menu
  // A domain missing here is reachable only from the header's Switch App grid: add every new app.
  const domainOrder: DomainKey[] = [
    'announcements',
    'bookings',
    'budgets',
    'callcentre',
    'campus-report',
    'rides',
    'salvation',
    'departments',
    'events',
    'feedback',
    'forms',
    'locations',
    'meetings',
    'members',
    'procedures',
    'procurements',
    'projects',
    'resources',
    'social-media',
    'store',
    'tasks',
    'vendors',
  ];

  return domainOrder
    .filter((key) => key !== 'demo') // Demo is in Admin section
    .map((domainKey) => {
      const domain = DOMAINS[domainKey];
      if (!domain) return null;

      const menuItem: MenuItem = {
        id: `app-${domainKey}`,
        label: domain.label,
        path: domain.basePath,
        icon: resizeIcon(domain.icon),
        requiredRoles: domain.requiredRoles,
        requiredPermissions: domain.requiredPermissions,
        requireAll: domain.requireAll,
        strictAccess: domain.strictAccess,
      };

      // Add visibility check if roles or permissions are defined
      if (domain.requiredRoles || domain.requiredPermissions) {
        menuItem.visible = createVisibilityCheck({
          roles: domain.requiredRoles,
          permissions: domain.requiredPermissions,
          requireAll: domain.requireAll,
          strictAccess: domain.strictAccess,
        });
      }

      // Live count badge — pulled from the domain's primary badge store
      // (synchronously via getState). Falls back to the static
      // `domain.badge` so test/demo configs without a subscriber still
      // render their placeholder. countSelector returns null when the
      // count is 0, hiding the badge.
      menuItem.countSelector = () => {
        const live = getDomainPrimaryBadgeCount(domainKey);
        if (live != null && live > 0) return live;
        if (typeof domain.badge === 'number' && domain.badge > 0) return domain.badge;
        if (typeof domain.badge === 'string' && domain.badge.length > 0) return domain.badge;
        return null;
      };
      menuItem.badgeColor =
        getDomainPrimaryBadgeColor(domainKey) ?? domain.badgeColor ?? 'blue';

      return menuItem;
    })
    .filter((item): item is MenuItem => item !== null);
}

/**
 * Generate User and Admin sections from GLOBAL_MENU_SECTIONS.
 * This ensures the menu structure is synced from domains.tsx.
 */
function generateSectionsFromConfig(): MenuSection[] {
  return GLOBAL_MENU_SECTIONS.filter((section) => section.label !== 'App').map((section) => ({
    id: `${section.label.toLowerCase()}-section`,
    label: section.label,
    collapsible: false,
    defaultOpen: section.defaultOpen ?? true,
    items: section.items.map((item) => {
      const menuItem: MenuItem = {
        id: `${section.label.toLowerCase()}-${item.label.toLowerCase().replace(/\s+/g, '-')}`,
        label: item.label,
        path: item.path,
        icon: item.icon,
        requiredRoles: item.requiredRoles,
        requiredPermissions: item.requiredPermissions,
        requireAll: item.requireAll,
        strictAccess: item.strictAccess,
      };

      // Add visibility check if roles or permissions are defined
      if (item.requiredRoles || item.requiredPermissions) {
        menuItem.visible = createVisibilityCheck({
          roles: item.requiredRoles,
          permissions: item.requiredPermissions,
          requireAll: item.requireAll,
          strictAccess: item.strictAccess,
        });
      }

      // Special case: the admin "Users" tile gets a live locked-out
      // count from <UserBadgeSubscriber />. Falls back to whatever the
      // static config carries.
      if (section.label === 'Admin' && item.label === 'Users') {
        menuItem.countSelector = () => {
          const live = useUserBadgesStore.getState().lockedOut;
          if (live > 0) return live;
          if (typeof item.badge === 'number' && item.badge > 0) return item.badge;
          return null;
        };
        menuItem.badgeColor = 'red';
      } else if (item.badge !== undefined) {
        menuItem.countSelector = () => item.badge as number;
        menuItem.badgeColor = item.badgeColor || 'blue';
      }

      return menuItem;
    }),
  }));
}

/**
 * Global menu provider implementation.
 *
 * Derives menu structure from domains.tsx configuration to ensure
 * role/permission requirements are synced from a single source of truth.
 */
class GlobalMenuProviderImpl implements GlobalMenuProvider {
  id = 'global';

  getMenuConfig() {
    // Generate App section from DOMAINS configuration
    const appSection: MenuSection = {
      id: 'app-section',
      label: 'App',
      collapsible: false,
      defaultOpen: true,
      items: generateAppMenuItems(),
    };

    // Generate User and Admin sections from GLOBAL_MENU_SECTIONS
    const otherSections = generateSectionsFromConfig();

    // Add Demo Components to Admin section
    const adminSection = otherSections.find((s) => s.id === 'admin-section');
    if (adminSection) {
      const demoDomain = DOMAINS['demo'];
      if (demoDomain) {
        adminSection.items.push({
          id: 'admin-demo',
          label: demoDomain.label,
          path: demoDomain.basePath,
          icon: <IconComponents size={16} />,
          requiredRoles: demoDomain.requiredRoles,
          requiredPermissions: demoDomain.requiredPermissions,
          visible: createVisibilityCheck({
            roles: demoDomain.requiredRoles,
            permissions: demoDomain.requiredPermissions,
          }),
        });
      }
    }

    const sections: MenuSection[] = [appSection, ...otherSections];

    return { sections };
  }

  initialize() {
    // Optional: Add initialization logic here
    // e.g., subscribe to stores, setup listeners, etc.
    console.log('[GlobalMenuProvider] Initialized');
  }

  destroy() {
    // Optional: Add cleanup logic here
    // e.g., unsubscribe from stores, cleanup listeners, etc.
    console.log('[GlobalMenuProvider] Destroyed');
  }
}

/**
 * Singleton instance of global menu provider.
 * Exported for registration in provider index.
 */
export const globalMenuProvider = new GlobalMenuProviderImpl();
